import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../config/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=email, 2=enter code, 3=new password, 4=done

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setMessage('A reset code has been sent to your email. Please check your inbox.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!resetCode.trim()) {
      setError('Please enter the reset code from your email.');
      return;
    }

    const passRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*]).{6,}$/;
    if (!passRegex.test(newPassword)) {
      setError('Password must be at least 6 characters with a letter, number, and special character.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { token: resetCode.trim(), new_password: newPassword });
      setMessage(res.data.message || 'Password reset successfully!');
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]";

  return (
    <div data-testid="forgot-password-page" className="pt-28 min-h-screen flex items-center justify-center bg-[#F7F8FA]">
      <div className="w-full max-w-md mx-6">
        <div className="bg-white rounded-xl p-8 md:p-10 shadow-lg border border-gray-100">
          <h1 className="text-3xl text-center mb-2 text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {step === 4 ? 'Password' : 'Forgot'} <span className="text-[#F57C20] italic">{step === 4 ? 'Reset' : 'Password'}</span>
          </h1>
          <p className="text-[#6B7280] text-sm text-center mb-8">
            {step === 1 && "Enter your registered email address"}
            {step === 2 && "Enter the reset code sent to your email and set a new password"}
            {step === 4 && "Your password has been reset successfully!"}
          </p>

          {error && <div data-testid="forgot-error" className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 mb-6 rounded-lg">{error}</div>}
          {message && <div data-testid="forgot-message" className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 mb-6 rounded-lg">{message}</div>}

          {/* Step 1: Enter email */}
          {step === 1 && (
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div>
                <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="forgot-email" className={inputCls} placeholder="your@email.com" />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full bg-[#F57C20] text-white py-4 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all disabled:opacity-50">
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          )}

          {/* Step 2: Enter code + new password */}
          {step === 2 && (
            <form onSubmit={handleVerifyAndReset} className="space-y-5">
              <div>
                <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Reset Code (from email)</label>
                <input type="text" value={resetCode} onChange={e => setResetCode(e.target.value)} required data-testid="reset-code-input" className={inputCls} placeholder="Paste the code from your email" />
              </div>
              <div>
                <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">New Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} required data-testid="reset-new-password" className={inputCls + " pr-12"} placeholder="Min 6 chars: letter + number + special" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F57C20] transition-colors">
                    {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full bg-[#F57C20] text-white py-4 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all disabled:opacity-50">
                {loading ? 'Resetting...' : 'Set New Password'}
              </button>
              <button type="button" onClick={() => { setStep(1); setError(''); setMessage(''); }} className="w-full text-[#6B7280] text-sm hover:text-[#F57C20] transition-colors">
                Didn't receive the code? Try again
              </button>
            </form>
          )}

          {/* Step 4: Done */}
          {step === 4 && (
            <Link to="/login" data-testid="back-to-login-btn" className="block w-full bg-[#F57C20] text-white py-4 text-center text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all">
              Go to Login
            </Link>
          )}

          {step < 4 && (
            <p className="text-[#6B7280] text-sm text-center mt-6">
              Remember your password?{' '}
              <Link to="/login" className="text-[#F57C20] font-semibold hover:underline">Sign In</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
